<template>
  <div class="tag-heatmap-mobile">
    <div class="chart-card" v-loading="loading">
      <div class="card-header">
        <span class="card-title">标签云图</span>
        <div class="card-actions">
          <el-select
            v-model="metricType"
            placeholder="指标"
            class="header-select metric-select"
            @change="handleMetricChange">
            <el-option label="平均分" value="avgScore" />
            <el-option label="正确率" value="accuracyRate" />
            <el-option label="订正率" value="correctionRate" />
          </el-select>
          <el-select
            v-model="timeRange"
            placeholder="时间"
            class="header-select time-select"
            @change="handleTimeRangeChange">
            <el-option label="全部" value="all" />
            <el-option label="最近一月" value="1m" />
            <el-option label="最近三月" value="3m" />
            <el-option label="最近半年" value="6m" />
            <el-option label="最近一年" value="1y" />
          </el-select>
          <el-button type="text" icon="el-icon-download" @click="handleExport" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
        </div>
      </div>
      
      <div class="chart-container" ref="heatmapChart"></div>
      
      <!-- 颜色图例 -->
      <div class="legend-container">
        <div class="legend-title">{{ metricLabel }}：</div>
        <div class="legend-items">
          <div class="legend-item">
            <span class="legend-color" style="background: #F56C6C;"></span>
            <span class="legend-text">0-20</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #E6A23C;"></span>
            <span class="legend-text">20-40</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #F7BA2A;"></span>
            <span class="legend-text">40-60</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #67C23A;"></span>
            <span class="legend-text">60-80</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #5CB87A;"></span>
            <span class="legend-text">80-100</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose">
      <div slot="title" class="dialog-header">
        <span>标签云图</span>
        <div class="dialog-operations">
          <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'

export default {
  name: 'TagHeatmapMobile',
  data() {
    return {
      loading: false,
      timeRange: 'all',
      metricType: 'avgScore',
      heatmapChart: null,
      fullscreenChart: null,
      heatmapData: [],
      dialogVisible: false
    }
  },
  computed: {
    metricLabel() {
      const labels = {
        'avgScore': '平均分',
        'accuracyRate': '正确率',
        'correctionRate': '订正率'
      }
      return labels[this.metricType] || '平均分'
    },
    metricUnit() {
      return this.metricType === 'avgScore' ? '分' : '%'
    }
  },
  mounted() {
    this.initChart()
    this.loadData()
    
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('orientationchange', this.handleResize)
  },
  beforeDestroy() {
    if (this.heatmapChart) {
      this.heatmapChart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('orientationchange', this.handleResize)
  },
  methods: {
    initChart() {
      if (this.heatmapChart) {
        this.heatmapChart.dispose()
      }
      
      this.heatmapChart = echarts.init(this.$refs.heatmapChart)
      this.updateChart()
    },
    
    async loadData() {
      this.loading = true
      
      try {
        const params = {}
        
        // 根据timeRange生成dateRange
        const dateRange = this.getDateRangeFromTimeRange()
        if (dateRange) {
          params['dateRange[]'] = dateRange
        }
        
        const response = await request({
          url: '/api/tag-heatmap/data',
          method: 'get',
          params
        })
        
        if (response.code === 200) {
          this.heatmapData = response.data || []
          this.updateChart()
          if (this.dialogVisible && this.fullscreenChart) {
            this.updateFullscreenChart()
          }
        } else {
          this.$message.error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    // 将timeRange转换为dateRange
    getDateRangeFromTimeRange() {
      if (this.timeRange === 'all') {
        return null
      }
      
      const end = new Date()
      const start = new Date()
      
      switch(this.timeRange) {
        case '1m':
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
          break
        case '3m':
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
          break
        case '6m':
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
          break
        case '1y':
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
          break
        default:
          return null
      }
      
      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      
      return [formatDate(start), formatDate(end)]
    },
    
    updateChart() {
      if (!this.heatmapChart || !this.heatmapData.length) return
      
      const option = this.getChartOption()
      this.heatmapChart.setOption(option, true)
    },
    
    // 获取图表配置
    getChartOption() {
      const chartData = this.heatmapData.map(item => ({
        name: item.name,
        value: item.value,
        metricValue: this.getMetricValue(item),
        itemStyle: {
          color: this.getColor(this.getMetricValue(item))
        },
        stats: item.stats
      }))
      
      return {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const data = params.data
            if (!data) return ''
            
            const metricValue = data.metricValue || 0
            const stats = data.stats || { correctCount: 0, wrongCount: 0, correctedCount: 0 }
            
            return `
              <div style="padding: 2px;">
                <div style="font-weight: bold; margin-bottom: 4px; font-size: 11px;">${data.name || ''}</div>
                <div style="font-size: 10px;">题目数: ${data.value || 0}</div>
                <div style="font-size: 10px;">${this.metricLabel}: ${metricValue.toFixed(1)}${this.metricUnit}</div>
                <div style="margin-top: 3px; padding-top: 3px; border-top: 1px solid #eee; font-size: 9px; color: #999;">
                  正确: ${stats.correctCount} | 错: ${stats.wrongCount} | 已订正: ${stats.correctedCount}
                </div>
              </div>
            `
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 10
          }
        },
        series: [
          {
            type: 'treemap',
            data: chartData,
            width: '100%',
            height: '100%',
            roam: false,
            nodeClick: false,
            breadcrumb: {
              show: false
            },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => {
                const data = params.data
                if (!data) return ''
                const metricValue = (data.metricValue || 0).toFixed(this.metricType === 'avgScore' ? 1 : 1)
                
                // 简单显示3行内容
                return `${data.name}\n${data.value}题\n${metricValue}${this.metricUnit}`
              },
              fontSize: 10,
              fontWeight: 'bold',
              color: '#fff',
              lineHeight: 14,
              textShadowBlur: 2,
              textShadowColor: 'rgba(0, 0, 0, 0.5)'
            },
            upperLabel: {
              show: false
            },
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 0,
              gapWidth: 0.75
            },
            emphasis: {
              label: {
                fontSize: 11,
                fontWeight: 'bold'
              },
              itemStyle: {
                shadowBlur: 8,
                shadowColor: 'rgba(0,0,0,0.3)',
                borderColor: '#333',
                borderWidth: 1
              }
            }
          }
        ]
      }
    },
    
    getMetricValue(item) {
      if (this.metricType === 'avgScore') {
        return item.avgScore
      } else if (this.metricType === 'accuracyRate') {
        return item.accuracyRate
      } else if (this.metricType === 'correctionRate') {
        return item.correctionRate
      }
      return 0
    },
    
    getColor(value) {
      if (value >= 80) return '#5CB87A'
      if (value >= 60) return '#67C23A'
      if (value >= 40) return '#F7BA2A'
      if (value >= 20) return '#E6A23C'
      return '#F56C6C'
    },
    
    handleTimeRangeChange() {
      this.loadData()
    },
    
    handleMetricChange() {
      this.updateChart()
      if (this.dialogVisible && this.fullscreenChart) {
        this.updateFullscreenChart()
      }
    },
    
    // 导出图片
    handleExport() {
      if (!this.heatmapChart) {
        this.$message.warning('图表未加载完成')
        return
      }
      
      try {
        const imgData = this.heatmapChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#ffffff'
        })
        
        const link = document.createElement('a')
        const fileName = `标签云图_${this.metricLabel}_${new Date().toISOString().slice(0, 10)}.png`
        link.href = imgData
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('图片下载成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },
    
    // 全屏显示
    handleFullscreen() {
      this.dialogVisible = true
    },
    
    // 关闭全屏
    closeDialog() {
      this.dialogVisible = false
    },
    
    // 全屏弹窗打开
    handleDialogOpen() {
      this.$nextTick(() => {
        if (this.fullscreenChart) {
          this.fullscreenChart.dispose()
        }
        
        this.fullscreenChart = echarts.init(this.$refs.fullscreenChart)
        this.updateFullscreenChart()
        
        setTimeout(() => {
          if (this.fullscreenChart) {
            this.fullscreenChart.resize()
          }
        }, 100)
      })
    },
    
    // 全屏弹窗关闭
    handleDialogClose() {
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    
    handleDialogClosed() {
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    
    // 更新全屏图表
    updateFullscreenChart() {
      if (!this.fullscreenChart || !this.heatmapData.length) return
      
      const option = this.getChartOption()
      this.fullscreenChart.setOption(option, true)
    },
    
    // 导出全屏图片
    handleExportFullscreen() {
      if (!this.fullscreenChart) {
        this.$message.warning('图表未加载完成')
        return
      }
      
      try {
        const imgData = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#ffffff'
        })
        
        const link = document.createElement('a')
        const fileName = `标签云图_全屏_${this.metricLabel}_${new Date().toISOString().slice(0, 10)}.png`
        link.href = imgData
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('图片下载成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },
    
    handleResize() {
      setTimeout(() => {
        if (this.heatmapChart) {
          this.heatmapChart.resize()
        }
      }, 100)
    }
  }
}
</script>

<style scoped>
.tag-heatmap-mobile {
  width: 100%;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  height: 40px;
  box-sizing: border-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
  margin-bottom: 0;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-actions .el-button {
  font-size: 16px;
  padding: 0;
  color: #ffffff;
}

.card-actions .el-button:hover {
  color: #BD6C73;
}

.header-select {
  width: 80px;
}

.metric-select {
  width: 70px;
}

.header-select :deep(.el-input__inner) {
  font-size: 11px;
  padding: 0 8px 0 4px;
  height: 26px;
  line-height: 26px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  border-radius: 4px;
}

.header-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.header-select :deep(.el-input__icon) {
  color: #fff;
  line-height: 26px;
}

.header-select :deep(.el-input__suffix) {
  right: 2px;
}

/* 下拉框选中项颜色 */
.header-select :deep(.el-select-dropdown__item.selected) {
  color: #67C23A;
}

.chart-container {
  width: 100%;
  height: 400px;
  padding: 12px;
  box-sizing: border-box;
}

.legend-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border-top: 1px solid #ebeef5;
  margin-top: 8px;
  gap: 6px;
}

.legend-title {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 4px;
}

.legend-items {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-color {
  width: 14px;
  height: 14px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend-text {
  font-size: 10px;
  color: #606266;
}

/* 全屏弹窗样式 */
.chart-dialog {
  margin: 0;
}

.chart-dialog .el-dialog {
  margin-top: 10vh;
  height: auto;
  display: flex;
  flex-direction: column;
  background-color: #2E365A;
}

.chart-dialog :deep(.el-dialog__header) {
  background-color: #2E365A;
  padding: 0;
  margin: 0;
  border-bottom: none;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  flex: 1;
  padding: 0;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  box-sizing: border-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
  font-size: 14px;
  font-weight: 500;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #fff;
  padding: 4px;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 70vh;
  padding: 0;
  box-sizing: border-box;
}

@media (max-width: 360px) {
  .chart-container {
    height: 350px;
  }
  
  .metric-selector :deep(.el-radio-button__inner) {
    padding: 6px 2px;
    font-size: 10px;
  }
  
  .legend-text {
    font-size: 9px;
  }
}
</style>

